import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";

export const supportedLangs = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'es', name: 'Español' },
  { code: 'it', name: 'Italiano' },
  { code: 'pt', name: 'Português' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'ru', name: 'Русский' },
  { code: 'zh-CN', name: '中文' },
  { code: 'ar', name: 'العربية' }
];

export default function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const changeLanguage = (code: string) => {
    let path = location.pathname;
    
    // Remove existing language prefix if present
    const pathParts = path.split('/').filter(Boolean);
    const firstPart = pathParts[0];
    
    if (supportedLangs.some(l => l.code === firstPart)) {
      pathParts.shift();
    }
    
    let newPath = '/' + pathParts.join('/');
    
    // Add new language prefix if not 'en'
    if (code !== 'en') {
      newPath = `/${code}${newPath === '/' ? '' : newPath}`;
    }
    
    navigate(newPath + location.search + location.hash);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-white hover:text-gold transition-colors cursor-pointer h-full"
        aria-label="Change Language"
      >
        <Globe size={20} />
        <span className="text-[10px] uppercase tracking-widest hidden lg:block">{i18n.language}</span>
        <ChevronDown size={14} className="hidden lg:block opacity-70" />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-6 bg-black/95 backdrop-blur-md border border-white/10 p-3 w-[280px] sm:w-[350px] grid grid-cols-2 gap-1 z-[999] rounded-sm shadow-2xl"
          >
            {supportedLangs.map(lang => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`w-full text-left px-3 py-2.5 text-[10px] sm:text-xs uppercase tracking-widest transition-colors cursor-pointer rounded-sm ${
                  i18n.language === lang.code ? "text-gold bg-white/10" : "text-white/80 hover:text-gold hover:bg-white/5"
                }`}
              >
                {lang.name}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
